// src/components/SuperAdminSchoolManagement.js
import React, { useState, useEffect } from 'react';
import { collection, doc, setDoc, serverTimestamp, getDoc, getDocs } from 'firebase/firestore';
import { Loader2 } from 'lucide-react';

const SuperAdminSchoolManagement = ({ db, setMessage, setError }) => {
  const [schoolName, setSchoolName] = useState('');
  const [customSchoolId, setCustomSchoolId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [schools, setSchools] = useState([]);
   const [loadingSchools, setLoadingSchools] = useState(true); // <--- THIS LINE WAS THE PROBLEM

  // Fonction pour charger les écoles existantes
  const fetchSchools = async () => {
    setLoadingSchools(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'schools'));
      const schoolsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSchools(schoolsList);
    } catch (error) {
      console.error("Erreur lors du chargement des écoles:", error);
      setError("Erreur lors du chargement des écoles.");
    } finally {
      setLoadingSchools(false);
    }
  };

  useEffect(() => {
    fetchSchools();
  }, [db]);

  const handleCreateSchool = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setIsSubmitting(true);

    if (!schoolName.trim()) {
      setError("Veuillez entrer un nom valide pour l'école.");
      setIsSubmitting(false);
      return;
    }

    if (!customSchoolId.trim()) {
      setError("Veuillez entrer un ID personnalisé pour l'école.");
      setIsSubmitting(false);
      return;
    }

    const validIdRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validIdRegex.test(customSchoolId.trim())) {
      setError("L'ID personnalisé ne doit contenir que des lettres, chiffres, tirets (-) ou underscores (_).");
      setIsSubmitting(false);
      return;
    }

    try {
      const schoolDocRef = doc(db, 'schools', customSchoolId.trim());
      const schoolDocSnap = await getDoc(schoolDocRef);

      if (schoolDocSnap.exists()) {
        setError(`L'ID d'école "${customSchoolId.trim()}" est déjà utilisé. Veuillez en choisir un autre.`);
        setIsSubmitting(false);
        return;
      }

      await setDoc(schoolDocRef, {
        name: schoolName.trim(),
        createdAt: serverTimestamp(),
      });

      setMessage(`École "${schoolName}" créée avec succès avec l'ID personnalisé: ${customSchoolId}.`);
      setSchoolName('');
      setCustomSchoolId('');
      fetchSchools();
    } catch (err) {
      console.error("Erreur lors de la création de l'école:", err);
      let errorMessage = "Échec de la création de l'école. Veuillez réessayer.";
      if (err.code === 'permission-denied') {
        errorMessage = "Permission refusée. Vérifiez vos règles de sécurité Firestore.";
      }
      setError(`Erreur: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Gestion des Écoles</h2>

      {/* Section pour la Création de Nouvelle École */}
      <div className="mb-8 p-4 border border-blue-200 rounded-lg bg-blue-50">
        <h3 className="text-xl font-semibold text-blue-800 mb-4">Créer une Nouvelle École</h3>
        <form onSubmit={handleCreateSchool} className="space-y-4">
          <div>
            <label htmlFor="schoolName" className="block text-sm font-medium text-gray-700">Nom de l'école</label>
            <input
              type="text"
              id="schoolName"
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="customSchoolId" className="block text-sm font-medium text-gray-700">ID Personnalisé de l'École</label>
            <input
              type="text"
              id="customSchoolId"
              value={customSchoolId}
              onChange={(e) => setCustomSchoolId(e.target.value)}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Ex: ecole_primaire_yoff"
            />
            {/* NOUVEAU MESSAGE D'ALERTE */}
            <p className="mt-1 text-xs text-red-600 font-medium bg-red-50 p-2 rounded-md border border-red-200">
              **Attention :** L'ID de l'école ne peut pas être modifié après sa création. Choisissez-le avec soin.
            </p>
            {/* FIN NOUVEAU MESSAGE D'ALERTE */}
          </div>
          <button
            type="submit"
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={isSubmitting}
          >
            {isSubmitting ? <Loader2 className="animate-spin mr-2" size={20} /> : 'Créer l\'école'}
          </button>
        </form>
      </div>

      {/* Section pour la Liste des Écoles Existantes */}
      <div>
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Écoles Existantes</h3>
        {loadingSchools ? (
          <div className="flex items-center justify-center text-gray-500">
            <Loader2 className="animate-spin mr-2" size={20} /> Chargement des écoles...
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {schools.length > 0 ? (
              schools.map((school) => (
                <li key={school.id} className="py-3 flex justify-between items-center">
                  <div>
                    <p className="text-lg font-medium text-gray-900">{school.name}</p>
                    <p className="text-sm text-gray-500">ID: **{school.id}**</p>
                  </div>
                  {/* Vous pouvez ajouter ici des boutons d'action (Visualiser, Supprimer) */}
                  {/* Il n'est pas recommandé d'ajouter un bouton "Modifier ID" ici */}
                </li>
              ))
            ) : (
              <p className="text-gray-600">Aucune école trouvée pour l'instant.</p>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default SuperAdminSchoolManagement;